import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FiSave, FiPlus, FiTrash2, FiMove } from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { HeaderContent, NavigationItem } from '../../../types/content'
import toast from 'react-hot-toast'

const HeaderEditor: React.FC = () => {
  const { content, loading, saving, saveContent } = useContent('header')
  const [formData, setFormData] = useState<HeaderContent>({
    logo: '',
    logoAlt: '',
    navigation: [],
    ctaButtonText: ''
  })

  useEffect(() => {
    if (content?.data) {
      const headerData = content.data as HeaderContent
      setFormData(headerData)
    }
  }, [content])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const success = await saveContent(formData)
    if (success) {
      toast.success('Header updated successfully!')
    }
  }

  const addNavigationItem = () => {
    const newItem: NavigationItem = {
      name: 'New Item',
      href: '#new',
      order: formData.navigation.length + 1
    }
    setFormData({
      ...formData,
      navigation: [...formData.navigation, newItem]
    })
  }

  const updateNavigationItem = (index: number, field: keyof NavigationItem, value: string | number) => {
    const updatedNavigation = formData.navigation.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    )
    setFormData({
      ...formData,
      navigation: updatedNavigation
    })
  }

  const removeNavigationItem = (index: number) => {
    const updatedNavigation = formData.navigation.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      navigation: updatedNavigation
    })
  }

  const moveNavigationItem = (index: number, direction: 'up' | 'down') => {
    const newNavigation = [...formData.navigation]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    
    if (targetIndex >= 0 && targetIndex < newNavigation.length) {
      [newNavigation[index], newNavigation[targetIndex]] = [newNavigation[targetIndex], newNavigation[index]]
      
      // Update order values
      newNavigation.forEach((item, i) => {
        item.order = i + 1
      })
      
      setFormData({
        ...formData,
        navigation: newNavigation
      })
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-neutral-200 rounded w-1/4"></div>
          <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-10 bg-neutral-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-neutral-800">Header Settings</h2>
          <p className="text-neutral-600 text-sm mt-1">
            Configure the site header, logo, navigation, and call-to-action button.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-neutral-800">Logo</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Logo URL
              </label>
              <input
                type="text"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="/assets/logo.png"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Logo Alt Text
              </label>
              <input
                type="text"
                value={formData.logoAlt}
                onChange={(e) => setFormData({ ...formData, logoAlt: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Company Name"
              />
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-neutral-800">Navigation</h3>
            <button
              type="button"
              onClick={addNavigationItem}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <FiPlus size={16} />
              <span>Add Item</span>
            </button>
          </div>

          <div className="space-y-3">
            {formData.navigation.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border border-neutral-200 rounded-lg"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => updateNavigationItem(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Home"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Link (href)
                    </label>
                    <input
                      type="text"
                      value={item.href}
                      onChange={(e) => updateNavigationItem(index, 'href', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="#home"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Order
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={item.order}
                        onChange={(e) => updateNavigationItem(index, 'order', parseInt(e.target.value))}
                        className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        min="1"
                      />
                      <div className="flex space-x-1">
                        <button
                          type="button"
                          onClick={() => moveNavigationItem(index, 'up')}
                          disabled={index === 0}
                          className="p-2 text-neutral-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <FiMove size={14} className="rotate-180" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveNavigationItem(index, 'down')}
                          disabled={index === formData.navigation.length - 1}
                          className="p-2 text-neutral-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <FiMove size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeNavigationItem(index)}
                          className="p-2 text-red-500 hover:text-red-700"
                          title="Remove item"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-neutral-800">Call-to-Action Button</h3>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Button Text
            </label>
            <input
              type="text"
              value={formData.ctaButtonText}
              onChange={(e) => setFormData({ ...formData, ctaButtonText: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Get in Touch"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-neutral-200">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSave size={16} />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </form>
    </div>
  )
}

export default HeaderEditor
